import React, { useState, useEffect } from 'react';
import { Upload, FileText, User, FolderOpen } from 'lucide-react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import LegalDisclaimerBanner from '../../components/navigation/LegalDisclaimerBanner';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';

import documentManagementService from '../../services/documentManagementService';
import employeeService from '../../services/employeeService';
import { useAuth } from '../../contexts/AuthContext';
import { useCompanySettings } from '../../contexts/CompanySettingsContext';
import EmployeeDocumentUploadModal from './components/EmployeeDocumentUploadModal';
import DocumentCategoryCard from './components/DocumentCategoryCard';
import EmployeeSearchPanel from './components/EmployeeSearchPanel';
import RecentUploadsPanel from './components/RecentUploadsPanel';

const EmployeeDocumentUploadPortal = () => {
  const { user } = useAuth();
  const { companySettings } = useCompanySettings();
  const companyName = companySettings?.companyName || 'HR Analytics';

  // State
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [recentUploads, setRecentUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // UI State
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [globalFilters, setGlobalFilters] = useState({});

  // Document categories with descriptions
  const documentCategories = [
    {
      id: 'contract',
      label: 'Employment Contracts',
      description: 'Previous and current employment contracts',
      icon: 'FileText',
      color: 'emerald'
    },
    {
      id: 'personal_passport',
      label: 'Passports',
      description: 'Passport copies for identification',
      icon: 'CreditCard',
      color: 'blue'
    },
    {
      id: 'personal_nid',
      label: 'National ID (NID)',
      description: 'National identification documents',
      icon: 'IdCard',
      color: 'purple'
    },
    {
      id: 'personal_license',
      label: 'Driver Licenses',
      description: 'Driver license for company vehicles',
      icon: 'Car',
      color: 'amber'
    },
    {
      id: 'compliance_housing',
      label: 'Housing Agreements',
      description: 'Company housing agreements and contracts',
      icon: 'Home',
      color: 'indigo'
    },
    {
      id: 'compliance_vehicle',
      label: 'Vehicle Agreements',
      description: 'Company vehicle usage agreements',
      icon: 'Truck',
      color: 'red'
    },
    {
      id: 'compliance_laptop',
      label: 'Laptop Agreements',
      description: 'Company laptop and equipment agreements',
      icon: 'Laptop',
      color: 'cyan'
    },
    {
      id: 'compliance_permit',
      label: 'Work Permits',
      description: 'Work permits and authorization documents',
      icon: 'FileCheck',
      color: 'green'
    },
    {
      id: 'compliance_visa',
      label: 'Work Visas',
      description: 'Work visa and immigration documents',
      icon: 'Globe',
      color: 'pink'
    },
    {
      id: 'personal_other',
      label: 'Other Documents',
      description: 'Other important employee documents',
      icon: 'FolderOpen',
      color: 'gray'
    }
  ];

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applySearch();
  }, [employees, searchTerm]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [employeesData, recentDocsData] = await Promise.all([
        employeeService?.getAllEmployees(),
        documentManagementService?.searchDocuments({
          limit: 10,
          orderBy: 'uploaded_at',
          orderDirection: 'desc'
        })
      ]);

      setEmployees(employeesData || []);
      setRecentUploads(recentDocsData || []);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const applySearch = () => {
    if (!searchTerm) {
      setFilteredEmployees(employees);
      return;
    }

    const term = searchTerm?.toLowerCase();
    const filtered = employees?.filter(emp =>
      emp?.firstName?.toLowerCase()?.includes(term) ||
      emp?.lastName?.toLowerCase()?.includes(term) ||
      emp?.employeeNumber?.toLowerCase()?.includes(term) ||
      emp?.department?.toLowerCase()?.includes(term) ||
      emp?.email?.toLowerCase()?.includes(term)
    );

    setFilteredEmployees(filtered);
  };

  const handleCategorySelect = (category) => {
    if (!selectedEmployee) {
      setError('Please select an employee first');
      setTimeout(() => setError(''), 3000);
      return;
    }

    setSelectedCategory(category);
    setShowUploadModal(true);
  };

  const handleUploadSuccess = () => {
    setShowUploadModal(false);
    setSelectedCategory(null);
    setSuccess('Document uploaded successfully');
    loadData();
    setTimeout(() => setSuccess(''), 3000);
  };

  const getCategoryMapping = (categoryId) => {
    // Map custom category IDs to database enum values
    if (categoryId?.startsWith('personal_')) {
      return 'personal';
    }
    if (categoryId?.startsWith('compliance_')) {
      return 'compliance';
    }
    return categoryId;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <GlobalFilterBar onFilterChange={setGlobalFilters} />
      <DataRefreshIndicator />
      <LegalDisclaimerBanner />
      <ComplianceAlertBanner />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Upload className="w-8 h-8 text-emerald-600" />
                Employee Document Upload Portal
              </h1>
              <p className="text-gray-600 mt-2">
                Upload and manage employee documents including contracts, identification, permits, and agreements
              </p>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-6 bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-lg">
            {success}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Employee Selection */}
          <div className="lg:col-span-1">
            <EmployeeSearchPanel
              employees={filteredEmployees}
              selectedEmployee={selectedEmployee}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              onEmployeeSelect={setSelectedEmployee}
              loading={loading}
            />

            {/* Recent Uploads */}
            <div className="mt-6">
              <RecentUploadsPanel
                uploads={recentUploads}
                loading={loading}
              />
            </div>
          </div>

          {/* Right Panel - Document Categories */}
          <div className="lg:col-span-2">
            {selectedEmployee ? (
              <div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {selectedEmployee?.firstName} {selectedEmployee?.lastName}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {selectedEmployee?.employeeNumber} • {selectedEmployee?.department}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Document Type</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {documentCategories?.map(category => (
                      <DocumentCategoryCard
                        key={category?.id}
                        category={category}
                        onClick={() => handleCategorySelect(category)}
                      />
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Select an Employee</h3>
                <p className="text-gray-600">Choose an employee from the list to upload documents</p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Upload Modal */}
      {showUploadModal && selectedEmployee && selectedCategory && (
        <EmployeeDocumentUploadModal
          employee={selectedEmployee}
          category={selectedCategory}
          categoryValue={getCategoryMapping(selectedCategory?.id)}
          onClose={() => {
            setShowUploadModal(false);
            setSelectedCategory(null);
          }}
          onSuccess={handleUploadSuccess}
        />
      )}
    </div>
  );
};

export default EmployeeDocumentUploadPortal;